import { useCallback } from 'react';
import styled from 'styled-components';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import { isValidUrl } from '@elfsight/lib-utils';
import { Button, Icon } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useTranslation } from 'react-i18next';
import type { SourceId } from '../../../constants';
import { getSourceMetaValue, meta } from '../../../constants';
import { useSocialFeedSettings } from '../../utils';
import { useHeaderData } from '../helpers';

interface HeaderFollowButtonProps {
  isMobile?: boolean;
}

export function HeaderFollowButton({ isMobile }: HeaderFollowButtonProps) {
  const { provider } = meta;
  const { t } = useTranslation();
  const { nativeIcon, icon, followButtonText } = getSourceMetaValue(
    provider as SourceId
  );

  const {
    headerFollowButtonSettings,
    googleAnalytics = [],
    widgetId = '',
    editMode
  } = useSocialFeedSettings();
  const {
    color: buttonColor,
    fontColor,
    mode,
    borderRadius,
    iconColor
  } = headerFollowButtonSettings ?? {};

  const { url = '' } = useHeaderData();

  const isCustomColor = iconColor?.type === 'custom';
  const isOutline = mode === 'outline';

  const customColor = isCustomColor ? iconColor?.color : 'currentColor';
  const finalIconColor = isOutline ? buttonColor : customColor;

  const iconSrc = isOutline || isCustomColor ? icon : nativeIcon;

  const IconLeft = useCallback(
    () => <Icon src={iconSrc} size={16} />,
    [iconSrc]
  );

  const handleClick = useCallback(() => {
    sendGoogleAnalyticsEvents({
      eventType: 'socialPlatformOpened',
      googleAnalyticsEvents: googleAnalytics,
      editMode,
      valueMapping: {
        widgetId,
        entryPoint: 'header'
      }
    });

    window.open(url, '_blank');
  }, [googleAnalytics, editMode, widgetId, url]);

  const isDisabled = !isValidUrl(url);
  const buttonText = followButtonText ? t(followButtonText) : t('Follow');
  const providerName = provider || 'social media';

  return (
    <Container>
      <StyledButton
        iconLeft={IconLeft}
        horizontalPadding={12}
        verticalPadding={6}
        borderRadius={borderRadius}
        color={buttonColor}
        mode={mode}
        fontColor={fontColor}
        font={{
          fontWeight: 700,
          fontSize: 14
        }}
        onClick={handleClick}
        disabled={isDisabled}
        aria-description="Opens in a new window"
        aria-label={`${buttonText} on ${providerName}`}
        $isMobile={isMobile}
        $iconColor={finalIconColor}
      >
        {buttonText}
      </StyledButton>
    </Container>
  );
}

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'header-follow-button',
    element: 'container'
  })
})`
  display: flex;
  align-items: center;
`;

const StyledButton = styled(Button).attrs({
  className: createReadableClass({
    component: 'header-follow-button'
  })
})<{
  $isMobile?: boolean;
  $iconColor?: string;
}>`
  line-height: 18px;

  ${({ $isMobile }) => ($isMobile ? 'align-self: flex-end' : '')};

  & svg {
    fill: ${({ $iconColor }) => $iconColor};
    width: 16px;
    height: 16px;
  }

  & .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;
